import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { CountrySelect } from '../components/CountrySelect.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { LanguageSwitch } from '../components/LanguageSwitch.tsx';
import { Button, EmptyState, Field, Skeleton } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { ApiError, request } from '../lib/api.ts';
import { moneyIn } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { VENDOR } from '../lib/vendor.ts';
import { BrandPanel } from './Login.tsx';

/**
 * Signing up for ERP PRO without anyone's help: the business (name, address, country, what it
 * does), a plan offered online, and the owner's own sign-in. The business starts on the plan's
 * free trial and the owner is signed in straight away.
 */

type Sector = 'restaurant' | 'bar_cafe' | 'hotel' | 'retail' | 'distribution' | 'construction' | 'public' | 'services' | 'other';
interface Plan {
  id: string;
  name: string;
  description: string | null;
  modules: string[];
  prices: Record<string, { month?: number; year?: number }>;
  trial_days: number;
}
interface Offer {
  open: boolean;
  plans: Plan[];
  sectors: Sector[];
  currency: string | null;
}

const SECTOR_ICONS: Record<Sector, IconName> = {
  restaurant: 'chef',
  bar_cafe: 'table',
  hotel: 'bed',
  retail: 'store',
  distribution: 'truck',
  construction: 'hammer',
  public: 'bank',
  services: 'users',
  other: 'apps',
};

/** The country from the browser's language ("fr-FR" → FR), as a first guess. */
const guessCountry = () => {
  for (const l of navigator.languages ?? [navigator.language]) {
    const m = /-([A-Z]{2})$/i.exec(l);
    if (m) return m[1]!.toUpperCase();
  }
  return '';
};
export const slugFrom = (name: string) =>
  name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/, '');

/** The price to show: in the country's money when the plan has it, else the first one it has. */
export function priceOf(plan: Plan, currency: string | null): { currency: string; month: number | null; year: number | null } | null {
  const code = currency && plan.prices[currency] ? currency : Object.keys(plan.prices)[0];
  if (!code) return null;
  const p = plan.prices[code]!;
  return { currency: code, month: p.month ?? null, year: p.year ?? null };
}

type Step = 0 | 1 | 2;
const STEPS: MessageKey[] = ['signup.step_business', 'signup.step_plan', 'signup.step_account'];

export function Signup() {
  const { signIn } = useSession();
  const [offer, setOffer] = useState<Offer | null>(null);
  const [failed, setFailed] = useState(false);
  const [step, setStep] = useState<Step>(0);
  const [business, setBusiness] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugState, setSlugState] = useState<{ slug: string; free: boolean; suggestion: string | null } | null>(null);
  const [country, setCountry] = useState(guessCountry);
  const [sector, setSector] = useState<Sector | ''>('');
  const [plan, setPlan] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [userTouched, setUserTouched] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [terms, setTerms] = useState(false);
  const [website, setWebsite] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const headRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    let live = true;
    request<Offer>('GET', `/api/signup${country ? `?country=${country}` : ''}`)
      .then((o) => {
        if (!live) return;
        setOffer(o);
        setPlan((p) => p || (o.plans.length === 1 ? o.plans[0]!.id : ''));
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [country]);

  // The address follows the name until it is changed by hand; whether it is free is asked as it changes.
  useEffect(() => {
    if (!slugTouched) setSlug(slugFrom(business));
  }, [business, slugTouched]);
  useEffect(() => {
    if (slug.length < 3) return setSlugState(null);
    const timer = window.setTimeout(() => {
      request<{ slug: string; free: boolean; suggestion: string | null }>('GET', `/api/signup/address?name=${encodeURIComponent(slug)}`)
        .then(setSlugState)
        .catch(() => setSlugState(null));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [slug]);
  useEffect(() => {
    if (!userTouched) setUsername(email.split('@')[0]!.toLowerCase().replace(/[^a-z0-9._-]/g, '').slice(0, 30));
  }, [email, userTouched]);
  // A new step is read from its top; the first one starts in the page as usual.
  const firstStep = useRef(true);
  useEffect(() => {
    if (firstStep.current) firstStep.current = false;
    else headRef.current?.focus();
  }, [step]);

  // A field's message goes once the field is changed.
  useEffect(() => {
    setErrors((e) => (Object.keys(e).length ? {} : e));
    setError(null);
  }, [business, slug, country, sector, plan, ownerName, email, username, password, terms]);

  const chosen = offer?.plans.find((p) => p.id === plan) ?? null;

  const check = (s: Step) => {
    const e: Record<string, string> = {};
    if (s === 0) {
      if (business.trim().length < 2) e.business = t('signup.e_business');
      if (!/^[a-z0-9][a-z0-9-]{2,40}$/.test(slug)) e.slug = t('signup.e_slug');
      else if (slugState?.slug === slug && !slugState.free) e.slug = t('signup.e_slug_taken');
      if (!country) e.country = t('signup.e_country');
      if (!sector) e.sector = t('signup.e_sector');
    } else if (s === 1) {
      if (!plan) e.plan = t('signup.e_plan');
    } else {
      if (ownerName.trim().length < 2) e.owner_name = t('signup.e_name');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = t('signup.e_email');
      if (!/^[a-z0-9._-]{2,30}$/.test(username)) e.username = t('signup.e_username');
      if (password.length < 12) e.password = t('signup.e_password');
      if (!terms) e.terms = t('signup.e_terms');
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = (e?: FormEvent) => {
    e?.preventDefault();
    setError(null);
    if (!check(step)) return;
    if (step < 2) return setStep((step + 1) as Step);
    void submit();
  };

  const submit = async () => {
    setBusy(true);
    try {
      const made = await request<{ slug: string; username: string }>('POST', '/api/signup', {
        business: business.trim(),
        slug,
        country,
        sector,
        plan,
        owner_name: ownerName.trim(),
        email: email.trim(),
        username,
        password,
        terms,
        website,
      });
      // The business is ready: on to it, signed in.
      history.replaceState(null, '', '/');
      try {
        await signIn(made.slug, made.username, password);
      } catch {
        window.location.assign('/');
      }
    } catch (err) {
      const msg = err instanceof ApiError && err.code !== 'offline' ? err.message : t('error.offline');
      // An address taken in the meantime is fixed on the first step.
      if (err instanceof ApiError && err.code === 'conflict' && step === 2 && /business ID/i.test(err.message)) {
        setStep(0);
        setErrors({ slug: msg });
      } else setError(msg);
      setBusy(false);
    }
  };

  const slugOk = slugState?.slug === slug && slugState.free ? t('signup.slug_free', { url: `${slug}` }) : null;

  return (
    <main className="login login-pro signup">
      <BrandPanel />
      <form className="login-card signup-card" onSubmit={next} noValidate>
        <div className="login-head">
          <h1 ref={headRef} tabIndex={-1}>
            {t('signup.title')}
          </h1>
          <LanguageSwitch />
        </div>
        {failed ? (
          <EmptyState icon="wifiOff" title={t('error.offline')} />
        ) : !offer ? (
          <Skeleton h={320} r="var(--radius-lg)" />
        ) : !offer.open ? (
          <EmptyState
            icon="clock"
            title={t('signup.closed')}
            body={t('signup.closed_body')}
            action={
              <Button variant="primary" icon="mail" onClick={() => window.location.assign(`mailto:${VENDOR.product.email}`)}>
                {VENDOR.product.email}
              </Button>
            }
          />
        ) : (
          <>
            <ol className="signup-steps" aria-label={t('signup.steps')}>
              {STEPS.map((k, i) => (
                <li key={k} className={i === step ? 'on' : i < step ? 'done' : ''} aria-current={i === step ? 'step' : undefined}>
                  <span className="signup-step-n" aria-hidden="true">
                    {i < step ? <Icon name="check" size={14} /> : i + 1}
                  </span>
                  <span>{t(k)}</span>
                </li>
              ))}
            </ol>

            {step === 0 ? (
              <>
                <p className="muted">{t('signup.business_intro')}</p>
                <Field label={t('signup.business')} error={errors.business} required>
                  <input className="input" value={business} maxLength={80} autoComplete="organization" onChange={(e) => setBusiness(e.target.value)} />
                </Field>
                <Field label={t('signup.slug')} hint={t('signup.slug_hint')} error={errors.slug} ok={errors.slug ? null : slugOk} required>
                  <input
                    className="input"
                    value={slug}
                    maxLength={41}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    onChange={(e) => {
                      setSlugTouched(true);
                      setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
                    }}
                  />
                </Field>
                {slugState?.slug === slug && !slugState.free && slugState.suggestion ? (
                  <p className="signup-suggest">
                    {t('signup.slug_taken')}{' '}
                    <button
                      type="button"
                      className="link"
                      onClick={() => {
                        setSlugTouched(true);
                        setSlug(slugState.suggestion!);
                        setErrors((e) => ({ ...e, slug: '' }));
                      }}
                    >
                      {t('signup.slug_use', { slug: slugState.suggestion })}
                    </button>
                  </p>
                ) : null}
                <Field label={t('signup.country')} hint={t('signup.country_hint')} error={errors.country} required>
                  <CountrySelect value={country} onChange={setCountry} />
                </Field>
                <fieldset className="signup-sectors">
                  <legend className="field-label">
                    {t('signup.sector')}{' '}
                    <span className="field-req" aria-hidden="true">
                      *
                    </span>
                  </legend>
                  <div className="signup-sector-grid">
                    {offer.sectors.map((s) => (
                      <label key={s} className={`signup-sector${sector === s ? ' on' : ''}`}>
                        <input type="radio" name="sector" value={s} checked={sector === s} onChange={() => setSector(s)} />
                        <Icon name={SECTOR_ICONS[s]} size={20} />
                        <span>{t(`platform.sector_${s}` as MessageKey)}</span>
                      </label>
                    ))}
                  </div>
                  {errors.sector ? (
                    <span className="field-msg" role="alert">
                      <Icon name="alert" size={14} /> {errors.sector}
                    </span>
                  ) : null}
                </fieldset>
              </>
            ) : step === 1 ? (
              <>
                <p className="muted">{t('signup.plan_intro')}</p>
                <div className="signup-plans" role="radiogroup" aria-label={t('signup.step_plan')}>
                  {offer.plans.map((p) => (
                    <PlanCard key={p.id} plan={p} currency={offer.currency} on={plan === p.id} onPick={() => setPlan(p.id)} />
                  ))}
                </div>
                {errors.plan ? (
                  <p className="form-error" role="alert">
                    <Icon name="alert" size={16} /> {errors.plan}
                  </p>
                ) : null}
                <p className="muted small">{t('signup.plan_note')}</p>
              </>
            ) : (
              <>
                <p className="muted">{t('signup.account_intro')}</p>
                <Field label={t('signup.owner_name')} error={errors.owner_name} required>
                  <input className="input" value={ownerName} maxLength={80} autoComplete="name" onChange={(e) => setOwnerName(e.target.value)} />
                </Field>
                <Field label={t('signup.email')} hint={t('signup.email_hint')} error={errors.email} required>
                  <input className="input" type="email" value={email} maxLength={200} autoComplete="email" onChange={(e) => setEmail(e.target.value)} />
                </Field>
                <Field label={t('login.username')} hint={t('signup.username_hint')} error={errors.username} required>
                  <input
                    className="input"
                    value={username}
                    maxLength={30}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    autoComplete="username"
                    onChange={(e) => {
                      setUserTouched(true);
                      setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ''));
                    }}
                  />
                </Field>
                <Field label={t('login.password')} hint={t('sec.new_password_hint')} error={errors.password} required>
                  <input
                    className="input"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    maxLength={200}
                    autoComplete="new-password"
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Field>
                <label className="check-row">
                  <input type="checkbox" checked={showPassword} onChange={(e) => setShowPassword(e.target.checked)} />
                  <span>{t('signup.show_password')}</span>
                </label>
                {/* Left empty by people; robots that fill every field give themselves away. */}
                <div className="signup-trap" aria-hidden="true">
                  <label>
                    Website
                    <input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
                  </label>
                </div>
                <label className="check-row">
                  <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} aria-invalid={errors.terms ? true : undefined} />
                  <span>
                    <span>
                      {t('signup.terms', { company: VENDOR.legal_name })}{' '}
                      <a className="link" href={`https://${VENDOR.product.website}/terms`} target="_blank" rel="noreferrer">
                        {t('signup.terms_link')}
                      </a>{' '}
                      ·{' '}
                      <a className="link" href={`https://${VENDOR.product.website}/privacy`} target="_blank" rel="noreferrer">
                        {t('signup.privacy_link')}
                      </a>
                    </span>
                  </span>
                </label>
                {errors.terms ? (
                  <span className="field-msg" role="alert">
                    <Icon name="alert" size={14} /> {errors.terms}
                  </span>
                ) : null}
                {chosen ? (
                  <p className="callout callout-info">
                    <Icon name="gift" size={18} />
                    <span>{tn('signup.summary', chosen.trial_days, { plan: chosen.name, business: business.trim() })}</span>
                  </p>
                ) : null}
              </>
            )}

            {error ? (
              <p className="form-error" role="alert">
                <Icon name="alert" size={16} /> {error}
              </p>
            ) : null}
            <div className="signup-actions">
              {step > 0 ? (
                <Button variant="ghost" icon="back" onClick={() => (setErrors({}), setError(null), setStep((step - 1) as Step))} disabled={busy}>
                  {t('common.back')}
                </Button>
              ) : null}
              <Button variant="primary" size="lg" type="submit" busy={busy} className="signup-next">
                {t(step === 2 ? 'signup.create' : 'signup.next')}
              </Button>
            </div>
          </>
        )}
        <p className="login-signup">
          {t('signup.have_account')}{' '}
          <a className="link" href="/">
            {t('login.submit')}
          </a>
        </p>
      </form>
    </main>
  );
}

function PlanCard({ plan, currency, on, onPick }: { plan: Plan; currency: string | null; on: boolean; onPick: () => void }) {
  const price = useMemo(() => priceOf(plan, currency), [plan, currency]);
  return (
    <label className={`signup-plan${on ? ' on' : ''}`}>
      <input type="radio" name="plan" value={plan.id} checked={on} onChange={onPick} />
      <span className="signup-plan-head">
        <strong>{plan.name}</strong>
        {plan.trial_days ? <span className="signup-plan-trial">{tn('signup.trial_days', plan.trial_days)}</span> : null}
      </span>
      {price?.month != null ? (
        <span className="signup-plan-price">
          <b>{moneyIn(price.month, price.currency)}</b> {t('signup.per_month')}
        </span>
      ) : price?.year != null ? (
        <span className="signup-plan-price">
          <b>{moneyIn(price.year, price.currency)}</b> {t('signup.per_year')}
        </span>
      ) : null}
      {price?.month != null && price.year != null ? <span className="muted small">{t('signup.or_yearly', { price: moneyIn(price.year, price.currency) })}</span> : null}
      {plan.description ? <span className="signup-plan-desc">{plan.description}</span> : null}
      <span className="muted small">{plan.modules.length ? tn('signup.apps', plan.modules.length) : t('signup.apps_core')}</span>
    </label>
  );
}
